import { inject } from 'inversify';

import { UnitManager } from './UnitManager';

import { service } from '@/decorators/service';
import { SignalrListener, SignalrMessage } from '@/decorators/signalr';
import { VehiclePosition } from '@/entities/VehiclePosition';
import { Logger } from '@/services/Logger';
import { Signalr } from '@/services/signalr/Signalr';
import { VehiclePositionStore } from '@/stores/VehiclePositionStore';
import { isValidPosition } from '@/utils/isValidPosition';
import { messageOrderComparision } from '@/utils/messageOrderComparision';
import { METRIC_INTERVALS } from '@/utils/metricsConstants';
import { normalize } from '@/utils/normalize';
import type { IVehiclePosition } from '~/api/IVehiclePosition';
import type { IVehicleMovedMsg } from '~/api/messages/IVehicleMovedMsg';
import type { IVehiclePositionHistoryMsg } from '~/api/messages/IVehiclePositionHistoryMsg';

@SignalrListener
@service(VehiclePositionManager)
export class VehiclePositionManager {
  @inject(Logger) protected logger: Logger;
  @inject(Signalr) protected signalr: Signalr;
  @inject(VehiclePositionStore) protected vehiclePositionStore: VehiclePositionStore;
  @inject(UnitManager) protected unitManager: UnitManager;

  //  AVL health metrics — flushed periodically to LogRocket
  private avlStats = {
    received: 0,
    dropped: 0,
    invalidPosition: 0,
    totalStalenessMs: 0,
    maxStalenessMs: 0,
  };

  private avlFlushTimer: ReturnType<typeof setInterval> | null = null;

  private ensureAvlFlushTimer(): void {
    if (this.avlFlushTimer) return;
    this.avlFlushTimer = setInterval(() => this.flushAvlStats(), METRIC_INTERVALS.AVL_HEALTH_FLUSH_MS);
  }

  private flushAvlStats(): void {
    if (this.avlStats.received === 0 && this.avlStats.dropped === 0 && this.avlStats.invalidPosition === 0) return;

    const {
      received,
      dropped,
      invalidPosition,
      totalStalenessMs,
      maxStalenessMs,
    } = this.avlStats;
    this.logger.track('avl.health', {
      received,
      dropped,
      invalidPosition,
      avgStalenessMs: received > 0 ? Math.round(totalStalenessMs / received) : 0,
      maxStalenessMs,
      dropRate: received > 0 ? Math.round((dropped / (received + dropped)) * 1000) / 1000 : 0,
    });

    this.avlStats = {
      received: 0,
      dropped: 0,
      invalidPosition: 0,
      totalStalenessMs: 0,
      maxStalenessMs: 0,
    };
  }

  private toTimestampMs(value?: string): number {
    if (!value) {
      return 0;
    }

    const direct = Date.parse(value);
    if (Number.isFinite(direct)) {
      return direct;
    }

    const normalized = Date.parse(value.replace(' ', 'T'));
    return Number.isFinite(normalized) ? normalized : 0;
  }

  getBreadCrumbPoints(vehicleId?: string): Array<{ lat: number; lng: number }> {
    if (!vehicleId) {
      return [];
    }

    const vehiclePosition = this.vehiclePositionStore.find(vehicleId);
    const history = vehiclePosition?.positionHistory ?? [];
    if (!history.length) {
      return [];
    }

    const sorted = [...history].sort((a, b) => this.toTimestampMs(a.timestamp) - this.toTimestampMs(b.timestamp));

    const points = sorted
      .map((p) => p.position)
      .filter((p) => isValidPosition(p))
      .map((p) => ({ lat: p.lat, lng: p.lng }));

    // Remove consecutive duplicates to avoid stacking markers.
    const deduped: Array<{ lat: number; lng: number }> = [];
    for (const p of points) {
      const prev = deduped.at(-1);
      if (prev?.lat !== p.lat || prev?.lng !== p.lng) {
        deduped.push(p);
      }
    }

    return deduped;
  }

  reset() {
    this.vehiclePositionStore.set([]);
  }

  remove(vehicleId?: string) {
    if (!vehicleId) {
      return;
    }

    this.vehiclePositionStore.remove(vehicleId);
  }

  async upsertFromVehicleMovedMsg(message: IVehicleMovedMsg): Promise<void> {
    if (!isValidPosition(message.position)) {
      return;
    }

    const vehicleId = message.id;
    const existing = this.vehiclePositionStore.find(vehicleId);

    const point: IVehiclePosition = {
      altitude: message.altitude,
      bearing: message.bearing,
      speed: message.speed,
      timestamp: message.timestamp,
      position: message.position,
    };

    const prevHistory = existing?.positionHistory ?? [];
    const history = [...prevHistory];

    const lastTimestamp = history.at(-1)?.timestamp;
    let didAppend = false;

    // - avlType === 1 (historic): always append
    // - avlType === 0 (live): append only if strictly newer than last timestamp
    if (message.avlType === 1) {
      history.push(point);
      didAppend = true;
    }
    else {
      const isNewer = messageOrderComparision(message.timestamp, lastTimestamp);
      if (isNewer) {
        history.push(point);
        didAppend = true;
      }
    }

    // ignore older/out-of-order live messages entirely.
    if (message.avlType === 0 && !didAppend) {
      this.avlStats.dropped += 1;
      return;
    }

    this.avlStats.received += 1;

    const shouldUpdateCurrent = message.avlType === 0;
    const nextCurrent: IVehiclePosition | undefined = shouldUpdateCurrent
      ? {
        ...point,
        bearing: normalize(message.bearing) ?? existing?.current?.bearing ?? point.bearing,
      }
      : existing?.current;

    await this.unitManager.updateVehiclePosition(message);

    this.vehiclePositionStore.update(
      new VehiclePosition({
        vehicleId,
        avlType: message.avlType,
        current: nextCurrent,
        positionHistory: history,
        sentTime: message.sentTime,
      }),
    );
  }

  private mergeHistory(prev: IVehiclePosition[], incoming: IVehiclePosition[]) {
    const merged = [...prev, ...incoming]
      .filter((p) => isValidPosition(p.position));

    // De-dupe by (timestamp, lat, lng) to avoid duplicates from replay/history bursts.
    const deduped = new Map<string, IVehiclePosition>();
    for (const p of merged) {
      const key = `${p.timestamp}|${p.position.lat}|${p.position.lng}`;
      if (!deduped.has(key)) {
        deduped.set(key, p);
      }
    }

    const sorted = [...deduped.values()].sort((a, b) => {
      const aMs = new Date(a.timestamp).getTime();
      const bMs = new Date(b.timestamp).getTime();
      return (Number.isFinite(aMs) ? aMs : 0) - (Number.isFinite(bMs) ? bMs : 0);
    });

    return sorted;
  }

  @SignalrMessage('VehicleMovedMsg')
  protected async onVehicleMovedMsg(message: IVehicleMovedMsg) {
    this.ensureAvlFlushTimer();

    //  Track staleness: how old is this position when it arrives?
    if (message.timestamp) {
      const messageMs = this.toTimestampMs(message.timestamp);
      if (messageMs > 0) {
        const stalenessMs = Math.max(0, Date.now() - messageMs);
        this.avlStats.totalStalenessMs += stalenessMs;
        if (stalenessMs > this.avlStats.maxStalenessMs) {
          this.avlStats.maxStalenessMs = stalenessMs;
        }
      }
    }

    if (!isValidPosition(message.position)) {
      this.avlStats.invalidPosition += 1;
      return;
    }

    await this.upsertFromVehicleMovedMsg(message);
  }

  @SignalrMessage('VehiclePositionHistoryMsg')
  protected onVehiclePositionHistoryMsg(message: IVehiclePositionHistoryMsg) {
    const vehicleId = message.id;
    const existing = this.vehiclePositionStore.find(vehicleId);

    const prevHistory = existing?.positionHistory ?? [];
    const nextHistory = this.mergeHistory(prevHistory, message.positionHistory ?? []);

    this.vehiclePositionStore.update(
      new VehiclePosition({
        vehicleId,
        avlType: existing?.avlType,
        current: existing?.current ?? nextHistory.at(-1),
        positionHistory: nextHistory,
        sentTime: existing?.sentTime,
      }),
    );
  }
}
